
import {
  defineComponent, onMounted, ref, Ref, computed, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useDropZone } from '@vueuse/core';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import ItemMixed from '../components/items/ItemMixed.vue';
import { IMedia, IMixedData } from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
// eslint-disable-next-line import/no-cycle
import DriveService from '../services/DriveService';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import isPhoto from '@/helpers/fileDetector';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
import { NAVIGATION_PAGES, UPLOAD_PLACES } from '@/types/enums';
import UseUpload from '@/composables/UseUpload';
import AppNoMessage from '@/components/AppNoMessage.vue';
import AppHeaderUploadProgress from '@/components/AppHeaderUploadProgress.vue';
import AppDropZone from '@/components/AppDropZone.vue';

export default defineComponent({
  components: {
    ItemMixed,
    RefreshPull,
    AppNoMessage,
    AppHeaderUploadProgress,
    AppDropZone,
  },
  emits: ['viewFile'],
  setup(props, { emit }) {
    const route = useRoute();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const { onUploadUpdate } = UseUpload();

    const dataLoading: Ref<boolean> = ref(false);

    const isNetwork = computed(() => route.meta.isNetwork);

    const mixedLastPage = computed(() => (isNetwork.value ? driveStore.networkMixedData.lastPage : driveStore.mixedData.lastPage));
    const mixedCurrentPage = computed(() => (isNetwork.value ? driveStore.networkMixedData.page : driveStore.mixedData.page));
    const currentPage = computed(() => route.meta.navigationPage);

    const mixedData = computed(() => {
      if (driveStore.search) {
        return driveStore.searchMixedData;
      }
      return (isNetwork.value ? driveStore.networkMixedData.data : driveStore.mixedData.data);
    });

    const noMixedMessage = computed(() => {
      if (!dataLoading.value && !mixedData.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: isNetwork.value ? 'Nothing shared with you yet...' : 'No files here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    const {
      scroller, pageIdx, isPageLoading, onScroll, onCacheScroll, setLastPage,
    } = UsePagination();

    async function fetchCacheData() {
      if (isNetwork.value) {
        return driveStore.fetchNetworkMixedData();
      }
      return driveStore.fetchMixedData();
    }

    function refreshMixed() {
      if (isNetwork.value) {
        driveStore.networkMixedData.page = 0;
        driveStore.networkMixedData.lastPage = -1;
      } else {
        driveStore.mixedData.page = 0;
        driveStore.mixedData.lastPage = -1;
      }

      return fetchCacheData();
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshMixed);

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = () => {
        if (isNetwork.value) {
          return DriveService.getNetworkMixedData(pageIdx.value, driveStore.search);
        }
        return DriveService.getMixedData(pageIdx.value, driveStore.search);
      };

      return resolveRequest().then((response) => {
        if (currentSearch !== driveStore.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (pageIdx.value === 0) {
          driveStore.searchMixedData = response.results.data.items;
        } else {
          driveStore.searchMixedData.push(...response.results.data.items);
        }
        return response;
      });
    }

    function handleScroll() {
      if (loadingPull.value) {
        return;
      }
      const direction = driveStore.isGridPhotoView ? DIRECTION.RIGHT : DIRECTION.BOTTOM;
      if (driveStore.search) {
        onScroll(fetchSearchData, direction, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, direction, mixedLastPage.value, mixedCurrentPage.value);
      }
    }

    function openFile(file: IMedia) {
      emit('viewFile', file);
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 1000);
    });

    function deselectAll(items: IMixedData[]): IMixedData[] {
      return items.map((item) => ({
        ...item,
        selected: false,
      }));
    }

    watch(() => driveStore.isMixedSelector, (newVal, prevVal) => {
      if (prevVal && !newVal) {
        driveStore.mixedData.data = deselectAll(driveStore.mixedData.data);
        driveStore.searchMixedData = deselectAll(driveStore.searchMixedData);
      }
    });

    function splitMedia(mediaList: File[]) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }

    function onDrop(mediaList: File[] | null) {
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);

        const fileUploads = UploadService.toFileUploads(fileList as unknown as FileList);
        const photoUploads = UploadService.toFileUploads(photoList as unknown as FileList);

        const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED,
            id: folderId,
          }, folderId);
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO,
            id: folderId,
          }, folderId);
        }
      }
    }

    const { isOverDropZone } = useDropZone(scroller, onDrop);

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      driveStore.search = '';
      driveStore.folderFileAmount = [];
    });

    return {
      mixedData,
      scroller,
      mainStore,
      driveStore,
      dataLoading,
      isPageLoading,
      noMixedMessage,
      openFile,
      handleScroll,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
      isOverDropZone,
    };
  },
});
